import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import styles from './SearchDropdown.module.scss';
import classNames from 'classnames';
import { useTranslation } from 'next-i18next';

const SearchDropdown = ({
    items,
    activeItems,
    onItemClick,
    getActiveItemTitle,
    dropdownLabel,
    hideDropdownLabel = false,
    multiSelect = false,
}) => {
    const { t } = useTranslation('common');
    const [isOpen, setIsOpen] = useState(false);
    const dropdownElement = useRef(null);

    useEffect(() => {
        const handleKeyPress = (e) => {
            const getPrevSibling = (elem) => {
                const parent = elem.parentElement;

                if (!parent || !parent.previousSibling) {
                    return false;
                }
                return parent.previousSibling.querySelector('button');
            };

            const getNextSibling = (elem) => {
                const parent = elem.parentElement;

                if (!parent || !parent.nextSibling) {
                    return false;
                }

                return parent.nextSibling.querySelector('button');
            };

            const UP = 38;
            const DOWN = 40;
            const ESC = 27;
            const keyCode = e.keyCode;
            const currentElement = document.activeElement;
            const prevSibling = getPrevSibling(currentElement);
            const nextSibling = getNextSibling(currentElement);

            if (keyCode === DOWN && nextSibling) {
                nextSibling.focus();
            }

            if (keyCode === UP && prevSibling) {
                prevSibling.focus();
            }

            if (keyCode === ESC) {
                setIsOpen(false);
            }
        };

        const handleClickOutsideDropdown = (e) => {
            if (!isOpen) return;

            const clickedInsideDropdown = dropdownElement.current && dropdownElement.current.contains(e.target);

            if (!clickedInsideDropdown) {
                setIsOpen(false);
            }
        };

        const currentDropdown = dropdownElement.current;

        document.addEventListener('click', handleClickOutsideDropdown);
        currentDropdown.addEventListener('keyup', handleKeyPress);

        return () => {
            document.removeEventListener('click', handleClickOutsideDropdown);
            currentDropdown.removeEventListener('keyup', handleKeyPress);
        };
    }, [isOpen]);

    const searchDropdownClasses = classNames(styles['SearchDropdown'], {
        [styles['SearchDropdown--HideSubLabel']]: hideDropdownLabel,
    });

    const switchMenuClass = classNames(styles['SearchDropdown__Wrap'], {
        [styles['SearchDropdown__Wrap--Open']]: isOpen,
    });

    const containerClass = classNames(styles['SearchDropdown__Container'], {
        [styles['SearchDropdown__Container--Open']]: isOpen,
    });

    const buttonClass = classNames(styles['SearchDropdown__Button'], {
        [styles['SearchDropdown__Button--Open']]: isOpen,
    });

    const getButtonText = () => {
        if (activeItems.length === 0) {
            return t('search.allRegions');
        }

        if (multiSelect) {
            if (activeItems.length === 1) {
                const activeItem = items.find((item) => item.path === activeItems[0]);
                return activeItem ? activeItem.title : t('search.allRegions');
            }
            return `${t('search.allRegions')} (${activeItems.length})`;
        } else {
            const activeItem = items.find((item) => item.path === activeItems[0]);
            return activeItem ? activeItem.title : t('search.allRegions');
        }
    };

    return (
        <div className={searchDropdownClasses} ref={dropdownElement}>
            <div className={styles['SearchDropdown__SubLabel']}>{dropdownLabel}</div>
            <div className={containerClass}>
                <button
                    onClick={() => setIsOpen(!isOpen)}
                    data-choice={true}
                    aria-expanded={isOpen}
                    aria-controls="search-dropdown"
                    className={buttonClass}>
                    {getButtonText()}
                </button>
            </div>
            <ul id="search-dropdown" data-choice={true} aria-hidden={!isOpen} className={switchMenuClass}>
                {items.map((item) => {
                    const checkBoxClass = classNames(styles['SearchDropdown__Checkbox'], 'SearchDropdown__Checkbox', {
                        [styles['SearchDropdown__Checkbox--Active']]: activeItems.indexOf(item.path) > -1,
                    });
                    return (
                        <li className={styles['SearchDropdown__Choice']} key={item.path}>
                            <button
                                className={
                                    styles['SearchDropdown__ChoiceButton'] + ' ' + 'SearchDropdown__ChoiceButton'
                                }
                                onClick={() => {
                                    onItemClick(item);
                                }}>
                                <span className={checkBoxClass} data-choice={true} aria-hidden={true} />
                                {item.title}
                            </button>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};

SearchDropdown.propTypes = {
    items: PropTypes.array,
    activeItems: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    getActiveItemTitle: PropTypes.func,
    onItemClick: PropTypes.func,
    dropdownLabel: PropTypes.string,
    hideDropdownLabel: PropTypes.bool,
    multiSelect: PropTypes.bool,
};

SearchDropdown.defaultProps = {
    items: [],
    activeItems: [],
    getActiveItemTitle: () => {},
    onItemClick: () => {},
    dropdownLabel: '',
    hideDropdownLabel: false,
    multiSelect: false,
};

export default SearchDropdown;
